import Button from '@cobliteam/colib-ui/Button';
import Dialog, { DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@cobliteam/colib-ui/Dialog';
import Spinner from '@cobliteam/colib-ui/Spinner';
import { useTranslation } from '@cobliteam/i18n';
import type { ReactNode } from 'react';
export interface SetupGuideAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Only meaningful on the primary action (design-system Button spinner). */
  loading?: boolean;
  /** Ícone do botão primário (ex.: o avião de "Convidar usuários"). Renderizado à direita do label, como no Figma (AE-2500). */
  endIcon?: ReactNode;
  'data-testid'?: string;
}
export interface DialogSetupGuideShellProps {
  open: boolean;
  /** Fired by the X, Esc, overlay click, and the default Cancel link. */
  onClose: () => void;
  /** Heading of the modal — wired to aria-labelledby via Radix. */
  title: string;
  /** Grey subtitle under the title — wired to aria-describedby via Radix. */
  description?: string;
  /** `lg` = creation modal (900px, default) · `sm` = success/confirm modal (440px). */
  size?: 'sm' | 'lg';
  /** Content slot: the entity's form (or a pre-built success body). */
  children: ReactNode;
  /** Left-aligned "Cancelar" link. Defaults to the shared translation. */
  cancelLabel?: string;
  /** Defaults to `onClose`. */
  onCancel?: () => void;
  /** Hides the left Cancel link (keeps the footer layout so the primary stays right-aligned). */
  hideCancel?: boolean;
  /** Optional link shown to the right, before the submit button. */
  secondaryAction?: SetupGuideAction;
  /** Primary submit button. */
  primaryAction?: SetupGuideAction;
  /** Hides the footer entirely. */
  hideFooter?: boolean;
  /** Escape hatch: replaces the whole footer. Prefer the declarative props above. */
  footer?: ReactNode;
  /** Extra className for the body slot (e.g. custom form paddings). */
  bodyClassName?: string;
  'data-testid'?: string;
}

/**
 * Shared shell for the Setup Guide entity modals (Header / content slot / Footer),
 * built on the design-system Dialog. Each entity fills the slot with its own form and
 * declares the footer actions; the layout (Cancel left, secondary + submit right) is
 * fixed here so every entity modal stays consistent. See {@link DialogSetupGuideSuccess}
 * for the paired success screen.
 */
export const DialogSetupGuideShell = ({
  open,
  onClose,
  title,
  description,
  size = 'lg',
  children,
  cancelLabel,
  onCancel,
  hideCancel,
  secondaryAction,
  primaryAction,
  hideFooter,
  footer,
  bodyClassName,
  'data-testid': dataTestId,
}: DialogSetupGuideShellProps) => {
  const { t } = useTranslation('ui-business');
  const footerContent = footer ?? (
    <>
      {hideCancel ? (
        // Keep the left slot so justify-between still pushes the primary action to the right.
        <span />
      ) : (
        // `ghost` é o padrão de cancelamento das modais do painel (AE-2500) e é o que o Figma desenha:
        // texto na cor de ação, sem borda nem preenchimento.
        <Button variant='ghost' onClick={onCancel ?? onClose}>
          {cancelLabel ?? t('dialogs.setup_guide.cancel')}
        </Button>
      )}
      <div className='flex items-center gap-4'>
        {secondaryAction && (
          // `ghost`, o mesmo do "Cancelar": os dois são ações de escape do rodapé e precisam reagir
          // igual ao hover (AE-2519). Com `link` o hover só sublinhava, sem o fundo do ghost.
          <Button variant='ghost' onClick={secondaryAction.onClick} disabled={secondaryAction.disabled} data-testid={secondaryAction['data-testid']}>
            {secondaryAction.label}
          </Button>
        )}
        {primaryAction && (
          <Button disabled={primaryAction.loading || primaryAction.disabled} onClick={primaryAction.onClick} data-testid={primaryAction['data-testid']}>
            {primaryAction.label}
            {primaryAction.loading ? <Spinner /> : primaryAction.endIcon}
          </Button>
        )}
      </div>
    </>
  );
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent size={size} closeLabel={t('dialogs.setup_guide.close')} data-testid={dataTestId}>
        {/* items-start keeps the X aligned to the top of the stacked title + description. */}
        <DialogHeader className='flex-row items-start items-center justify-between pr-8'>
          <div className='flex flex-col gap-1'>
            <DialogTitle className='text-foreground'>{title}</DialogTitle>
            {description ? (
              <DialogDescription className='text-foreground'>{description}</DialogDescription>
            ) : (
              // Radix warns without a description; keep an accessible one when there is no visible subtitle.
              <DialogDescription className='sr-only'>{title}</DialogDescription>
            )}
          </div>
        </DialogHeader>
        <DialogBody className={bodyClassName}>{children}</DialogBody>
        {!hideFooter && <DialogFooter className='flex items-center justify-between gap-2'>{footerContent}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
};
